// frontend/src/components/players/PDFViewer.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSecureMedia } from '../../services/mediaService';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const QUALITY_LEVELS = {
  low: { label: 'اقتصادی', dpi: 100 },
  medium: { label: 'متوسط', dpi: 150 },
  high: { label: 'بالا', dpi: 200 },
  ultra: { label: 'عالی', dpi: 300 },
};

const PDFViewer = ({ mediaId, fileUrl, title }) => {
  // ✅ اگر mediaId null باشه، از fileUrl استخراج کن یا fallback بذار
  const effectiveMediaId = mediaId || (fileUrl ? fileUrl.match(/media_files\/(\d+)/)?.[1] || null : null);
  
  console.log('🔍 PDFViewer props:', { mediaId, effectiveMediaId, fileUrl, title });

  const { mediaUrl, isLoading: urlLoading, error: urlError, refetch: refetchMediaUrl } =
    useSecureMedia(effectiveMediaId);

  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [scale, setScale] = useState(1.2);
  const [isScannedPDF, setIsScannedPDF] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [quality, setQuality] = useState('medium');
  
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const renderTaskRef = useRef(null);
  const isMounted = useRef(true);

  const directPdf = fileUrl || mediaUrl;
  const proxyUrl = directPdf?.startsWith('https://s3.afra.kubit.cloud/')
    ? `https://farzaneh.app/api/proxy/media/?url=${encodeURIComponent(directPdf)}`
    : directPdf;

  // ✅ تشخیص نوع PDF - آستانه 100
  useEffect(() => {
    if (!proxyUrl) return;
    isMounted.current = true;
    setLoading(true);

    const detect = async () => {
      try {
        const pdf = await pdfjsLib.getDocument({ url: proxyUrl }).promise;
        if (!isMounted.current) return;
        
        const page = await pdf.getPage(1);
        const text = await page.getTextContent();
        const len = text.items.reduce((s, i) => s + i.str.length, 0);
        // ✅ آستانه 100: کمتر از 100 کاراکتر = اسکن شده
        const scanned = len < 100;
        
        console.log(`📄 PDF: ${scanned ? 'IMAGE' : 'CANVAS'} (${len} chars)`);
        
        setIsScannedPDF(scanned);
        setNumPages(pdf.numPages);
        
        if (scanned) {
          pdf.destroy();
        } else {
          setPdfDoc(pdf);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error:', err);
        if (isMounted.current) {
          // ✅ fallback: اگر تشخیص نشد، Canvas رو امتحان کن
          setIsScannedPDF(false);
          setLoading(false);
        }
      }
    };
    
    detect();
    return () => { isMounted.current = false; };
  }, [proxyUrl]);

  // ✅ Canvas render
  useEffect(() => {
    if (!pdfDoc || isScannedPDF || !canvasRef.current) return;
    
    let cancelled = false;
    
    (async () => {
      try {
        const page = await pdfDoc.getPage(pageNumber);
        const viewport = page.getViewport({ scale: 1 });
        
        const pw = canvasRef.current.parentElement?.clientWidth || 800;
        const ph = window.innerHeight - 180;
        const s = Math.max(0.5, Math.min(pw / viewport.width, ph / viewport.height, 2));
        
        const dpr = window.devicePixelRatio || 1;
        const vs = page.getViewport({ scale: s * dpr });
        
        const canvas = canvasRef.current;
        canvas.width = vs.width;
        canvas.height = vs.height;
        canvas.style.width = `${vs.width / dpr}px`;
        canvas.style.height = `${vs.height / dpr}px`;
        canvas.style.display = 'block';
        canvas.style.margin = '0 auto';
        
        const ctx = canvas.getContext('2d', { alpha: false });
        await page.render({ canvasContext: ctx, viewport: vs, background: '#fff' }).promise;
        
        if (!cancelled) setScale(s);
      } catch (err) {
        if (err.name !== 'RenderingCancelledException') console.error(err);
      }
    })();
    
    return () => { cancelled = true; };
  }, [pdfDoc, pageNumber]);

  // ✅ Image render - با effectiveMediaId
  useEffect(() => {
    if (!isScannedPDF || !effectiveMediaId) {
      console.log('❌ Image skipped: isScannedPDF=', isScannedPDF, 'effectiveMediaId=', effectiveMediaId);
      return;
    }
    
    const url = `/api/v1/products/pdf-to-image/${effectiveMediaId}/?page=${pageNumber}&quality=${quality}`;
    console.log('📸 Image URL:', url);
    setImageUrl(url);
  }, [isScannedPDF, pageNumber, quality, effectiveMediaId]);

  const changePage = useCallback((o) => {
    setPageNumber(p => Math.max(1, Math.min(p + o, numPages)));
  }, [numPages]);

  const goToPage = (p) => setPageNumber(Math.max(1, Math.min(p, numPages)));
  const zoomIn = () => setScale(s => Math.min(s + 0.2, 2.5));
  const zoomOut = () => setScale(s => Math.max(s - 0.2, 0.5));
  const cycleQuality = () => {
    const qs = Object.keys(QUALITY_LEVELS);
    setQuality(qs[(qs.indexOf(quality) + 1) % qs.length]);
  };

  const handleCtx = (e) => e.preventDefault();

  useEffect(() => {
    const h = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'p')) e.preventDefault();
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); changePage(1); }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); changePage(-1); }
      if (isScannedPDF && (e.key === '+' || e.key === '=')) cycleQuality();
      if (isScannedPDF && e.key === '-') {
        const qs = Object.keys(QUALITY_LEVELS);
        setQuality(qs[Math.max(0, qs.indexOf(quality) - 1)]);
      }
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [changePage, quality, isScannedPDF]);

  if (urlError || error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-red-500">{error || urlError}</p>
        <button onClick={refetchMediaUrl} className="bg-teal-600 text-white px-4 py-2 rounded">تلاش مجدد</button>
      </div>
    );
  }

  if (isScannedPDF === null || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  // ✅ Canvas mode - PDF متنی
  if (!isScannedPDF) {
    return (
      <div className="h-screen flex flex-col bg-gray-100" onContextMenu={handleCtx}>
        <div className="bg-white border-b-2 border-teal-700 px-4 py-3 flex justify-between items-center">
          <span className="text-teal-700 font-semibold">{title || 'PDF'}</span>
          <span className="text-sm">صفحه {pageNumber} از {numPages}</span>
          <div className="flex gap-2">
            <button onClick={zoomOut} className="px-2 py-1 border rounded text-sm">−</button>
            <span className="text-sm">{Math.round(scale * 100)}%</span>
            <button onClick={zoomIn} className="px-2 py-1 border rounded text-sm">+</button>
            <button onClick={() => changePage(-1)} disabled={pageNumber <= 1} className="px-2 py-1 border rounded text-sm">◀</button>
            <input type="number" min={1} max={numPages} value={pageNumber} onChange={(e) => goToPage(parseInt(e.target.value) || 1)} className="w-12 text-center border rounded text-sm" />
            <button onClick={() => changePage(1)} disabled={pageNumber >= numPages} className="px-2 py-1 border rounded text-sm">▶</button>
          </div>
        </div>
        <div ref={containerRef} className="flex-1 overflow-auto flex justify-center p-4">
          <canvas ref={canvasRef} />
        </div>
      </div>
    );
  }

  // ✅ Image mode - PDF اسکن شده
  if (!imageUrl) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50" onContextMenu={handleCtx}>
      <div className="bg-teal-800 text-white px-4 py-3 flex justify-between items-center">
        <button onClick={cycleQuality} className="px-2 py-1 rounded text-xs bg-gray-600">{QUALITY_LEVELS[quality]?.label}</button>
        <span className="text-xs">{QUALITY_LEVELS[quality]?.dpi} DPI | 📸 اسکن شده</span>
        <div className="flex gap-1">
          <button onClick={() => changePage(-1)} disabled={pageNumber <= 1} className="px-2 py-1 rounded text-xs">◀</button>
          <input type="number" min={1} max={numPages} value={pageNumber} onChange={(e) => goToPage(parseInt(e.target.value) || 1)} className="w-12 text-center bg-white/20 rounded text-xs" />
          <span className="text-xs">/ {numPages}</span>
          <button onClick={() => changePage(1)} disabled={pageNumber >= numPages} className="px-2 py-1 rounded text-xs">▶</button>
        </div>
      </div>
      <div ref={containerRef} className="flex-1 flex items-center justify-center p-2 overflow-auto">
        <img src={imageUrl} alt={`صفحه ${pageNumber}`} className="max-w-full shadow-xl" onError={() => setError('خطا در بارگذاری تصویر')} />
      </div>
    </div>
  );
};

export default PDFViewer;